import { useState, useEffect, useCallback } from 'react';
import { rmmAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Loader2, Gauge, Search, Users, Boxes, FileDown, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { fmtWhen } from './rmmUtils';

const Spin = () => <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>;
const ScoreBadge = ({ v }) => v == null ? <span className="text-xs text-slate-400">—</span> :
  <Badge className={v >= 80 ? 'bg-emerald-100 text-emerald-700' : v >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>{v}</Badge>;

const dl = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
};

export const MSPTab = ({ isAdmin }) => {
  const [summary, setSummary] = useState(null);
  const [health, setHealth] = useState(null);
  const [techOps, setTechOps] = useState([]);
  const [assets, setAssets] = useState([]);
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const load = useCallback(() => {
    Promise.all([rmmAPI.mspSummary(), rmmAPI.customerHealth(), rmmAPI.techOps(), rmmAPI.listAssets()])
      .then(([s, h, t, a]) => { setSummary(s.data); setHealth(h.data); setTechOps(t.data); setAssets(a.data); })
      .catch(() => setSummary({}));
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  if (!summary) return <Spin />;

  const search = () => {
    if (q.trim().length < 2) return;
    setSearching(true);
    rmmAPI.globalSearch(q).then((r) => setResults(r.data)).catch(() => toast.error('Search failed')).finally(() => setSearching(false));
  };
  const flagged = assets.filter((a) => a.recommendation);

  const stats = [
    ['Customers', summary.customers], ['Devices', `${summary.devices_online}/${summary.devices_total} online`],
    ['Critical alerts', summary.critical_alerts], ['Open tickets', summary.open_tickets],
    ['SLA breached', summary.tickets_sla_breached], ['Patch compliance', `${summary.patch_compliance_pct}%`],
    ['Security issues', summary.security_issues], ['Backup issues', summary.backup_issues],
    ['Network issues', summary.network_issues], ['M365 tenants', `${summary.m365_connected}/${summary.m365_tenants}`],
  ];

  return (
    <div className="space-y-6" data-testid="rmm-msp-tab">
      <div className="flex gap-2 items-center">
        <div className="relative flex-1 max-w-xl">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Global search: customers, devices, tickets, alerts, work orders, software…" value={q}
            onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && search()}
            className="pl-9 bg-white border-slate-200" data-testid="msp-search-input" />
        </div>
        <Button onClick={search} disabled={searching} className="bg-cyan-600 hover:bg-cyan-700" data-testid="msp-search-btn">
          {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}</Button>
        <Button variant="outline" data-testid="msp-exec-report-btn"
          onClick={() => rmmAPI.executiveReportPdf().then((r) => dl(r.data, 'executive-report.pdf')).catch(() => toast.error('Report failed'))}>
          <FileDown className="w-4 h-4 mr-1" />Executive PDF</Button>
      </div>

      {results && (
        <Card className="bg-white border-slate-200" data-testid="msp-search-results">
          <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 text-sm">
            {[['Customers', results.customers, (r) => r.name], ['Sites', results.sites, (r) => `${r.name} · ${r.customer_name || ''}`],
              ['Devices', results.devices, (r) => `${r.hostname} · ${r.status}`], ['Tickets', results.tickets, (r) => `${r.ticket_number} ${r.subject}`],
              ['Alerts', results.alerts, (r) => r.message], ['Work Orders', results.work_orders, (r) => `${r.wo_number || ''} ${r.customer_name || ''}`],
              ['Scripts', results.scripts, (r) => r.name], ['Software on devices', results.software_on_devices, (r) => r.hostname]]
              .filter(([, rows]) => rows?.length).map(([label, rows, fmt]) => (
                <div key={label}>
                  <p className="text-xs font-semibold text-slate-500 mb-1">{label}</p>
                  {rows.map((r, i) => <p key={i} className="text-slate-700 truncate text-xs py-0.5">{fmt(r)}</p>)}
                </div>))}
            {Object.values(results).every((v) => !Array.isArray(v) || !v.length) && <p className="text-slate-500">No matches for "{results.query}".</p>}
          </CardContent>
        </Card>)}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="msp-stat-cards">
        {stats.map(([l, v]) => (
          <Card key={l} className="bg-white border-slate-200">
            <CardContent className="p-3"><p className="text-xl font-bold text-slate-800">{v ?? 0}</p><p className="text-[11px] text-slate-500">{l}</p></CardContent>
          </Card>))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card className="bg-white border-slate-200">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Gauge className="w-4 h-4 text-cyan-500" />Customer Health Scores <span className="text-xs font-normal text-slate-400">(configurable weights · Security tab)</span></CardTitle></CardHeader>
          <CardContent className="space-y-1.5 max-h-96 overflow-y-auto" data-testid="msp-customer-health">
            {(health?.customers || []).map((c) => (
              <div key={c.customer_id} className="flex items-center justify-between text-sm p-2.5 rounded-lg border border-slate-200">
                <div className="flex-1">
                  <span className="font-medium text-slate-800">{c.customer_name}</span>
                  <span className="text-xs text-slate-500 ml-2">{c.devices} device(s)</span>
                  {c.trend?.length > 1 && (
                    <span className={`text-[11px] ml-2 ${c.trend.at(-1).score >= c.trend[0].score ? 'text-emerald-600' : 'text-red-600'}`}>
                      {c.trend.at(-1).score >= c.trend[0].score ? '▲' : '▼'} {Math.abs(c.trend.at(-1).score - c.trend[0].score)} (30d)
                    </span>)}
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-cyan-600" data-testid={`msp-report-${c.customer_name}`}
                    onClick={() => rmmAPI.customerReportPdf(c.customer_id).then((r) => dl(r.data, `report-${(c.customer_name || 'customer').replace(/\W+/g, '-')}.pdf`)).catch(() => toast.error('Report failed'))}>
                    <FileDown className="w-3.5 h-3.5 mr-1" />Report</Button>
                  <ScoreBadge v={c.score} />
                </div>
              </div>))}
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4 text-cyan-500" />Technician Operations (30d)</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto" data-testid="msp-tech-ops">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-2">Technician</th><th className="pr-2">Resolved</th><th className="pr-2">Open</th><th className="pr-2">RMM actions</th><th className="pr-2">Sessions</th><th>Devices</th></tr></thead>
              <tbody>
                {techOps.map((t) => (
                  <tr key={t.id} className="border-b border-slate-100">
                    <td className="py-1.5 pr-2 font-medium text-slate-800">{t.name} <span className="text-[10px] text-slate-400">{t.role}</span></td>
                    <td className="pr-2 text-emerald-600 font-semibold">{t.tickets_resolved}</td>
                    <td className="pr-2">{t.tickets_open}</td>
                    <td className="pr-2">{t.rmm_commands}</td>
                    <td className="pr-2">{t.remote_sessions}</td>
                    <td>{t.devices_managed}</td>
                  </tr>))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white border-slate-200">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Boxes className="w-4 h-4 text-cyan-500" />Asset Lifecycle & Warranty {flagged.length > 0 && <Badge className="bg-amber-100 text-amber-700">{flagged.length} need attention</Badge>}</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-2">Asset</th><th className="pr-2">Customer</th><th className="pr-2">Serial</th><th className="pr-2">Age</th><th className="pr-2">Warranty</th><th className="pr-2">Lifecycle</th><th>Recommendation</th></tr></thead>
            <tbody data-testid="msp-assets-table">
              {assets.slice(0, 25).map((a) => (
                <tr key={a.id} className="border-b border-slate-100" data-testid={`msp-asset-${a.hostname}`}>
                  <td className="py-1.5 pr-2 font-medium text-slate-800">{a.hostname} <span className="text-[10px] text-slate-400 capitalize">{a.device_type}</span></td>
                  <td className="pr-2 text-slate-600">{a.customer_name}</td>
                  <td className="pr-2 text-xs font-mono text-slate-500">{a.serial_number || '—'}</td>
                  <td className="pr-2">{a.age_years != null ? `${a.age_years}y` : '—'}</td>
                  <td className="pr-2">{a.warranty_days_left == null ? '—' :
                    <Badge className={a.warranty_days_left < 0 ? 'bg-red-100 text-red-700' : a.warranty_days_left <= 60 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}>
                      {a.warranty_days_left < 0 ? `expired ${Math.abs(a.warranty_days_left)}d` : `${a.warranty_days_left}d left`}</Badge>}</td>
                  <td className="pr-2">
                    {isAdmin ? (
                      <select value={a.lifecycle_state} data-testid={`msp-lifecycle-${a.hostname}`}
                        className="text-xs border border-slate-200 rounded-md px-1.5 py-1 bg-white capitalize"
                        onChange={(e) => rmmAPI.setLifecycle(a.id, { lifecycle_state: e.target.value }).then(() => { toast.success('Lifecycle updated'); load(); })}>
                        {['discovered', 'enrolled', 'active', 'attention', 'retirement_planned', 'retired'].map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                      </select>
                    ) : <Badge variant="outline" className="capitalize text-[10px]">{a.lifecycle_state?.replace('_', ' ')}</Badge>}
                  </td>
                  <td className="text-xs text-amber-700">{a.recommendation || ''}</td>
                </tr>))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      <p className="text-[11px] text-slate-400 flex items-center gap-1"><Sparkles className="w-3 h-3" />AI assistance available per alert, device and ticket — clearly labeled, recommendations only.</p>
    </div>
  );
};
